import { t } from '@reos/i18n';

/**
 * Krok O8: nazev zakazky pro zobrazeni (kanban, seznam i tabulka pouzivaji
 * tuhle jednu funkci).
 *
 * Cast zakazek nema nazev z dat a nese misto nej zastupny retezec s internim
 * id — na pilotnim tenantu je to "Nemovitost 01a05672", kde `01a05672` je
 * prefix tenant id. Interni id se makleri neukazuje NIKDY; poradi nahrad je
 * nazev z dat -> adresa nemovitosti -> „Bez nazvu" (interni id jen v `title`
 * atributu, aby slo zakazku dohledat pri podpore).
 */

/** Kanonicke UUID; vsechna nase id chodi malymi pismeny (`newId()` z @reos/ids). */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Urez id: souvisly hexadecimalni retezec od 8 znaku, opet jen male pismeno. */
const ID_TOKEN_RE = /^[0-9a-f]{8,}$/;
/** Obycejne slovo (bez cislic a interpunkce) — "Nemovitost", "Property". */
const WORD_RE = /^\p{L}+$/u;

/**
 * Je titulek jen zastupny text s internim id? Zamerne uzke pravidlo: id token
 * musi byt POSLEDNI a pred nim smi stat nejvys jedno holé slovo. Skutecne
 * adresy ("Sokolovska 168, 186 00 Praha", "Hrabanov, parcely 2480/6") ani
 * nazvy ("Byt 5+1 Praha") pod nej nespadnou.
 */
export function isInternalIdTitle(title: string): boolean {
  const trimmed = title.trim();
  if (trimmed === '') return true;
  const parts = trimmed.split(/\s+/);
  const last = parts[parts.length - 1] ?? '';
  if (!UUID_RE.test(last) && !ID_TOKEN_RE.test(last)) return false;
  return parts.length <= 2 && parts.slice(0, -1).every((word) => WORD_RE.test(word));
}

export interface CaseDisplayName {
  readonly text: string;
  /** `title` atribut radku/karty — vyplneny jen u zastupneho nazvu. */
  readonly hint: string | undefined;
}

export interface CaseNameTexts {
  readonly untitled: string;
  readonly untitledHint: (id: string) => string;
}

/**
 * Texty nahradniho nazvu — JEDNA kopie pro vsechny volajici (krok APP2 bod 1).
 * `locale: 'cs'` stejne jako v `lib/texts.ts`: tahle vrstva nema React kontext,
 * ze ktereho by jazyk uzivatele poznala.
 */
export const CASE_NAME_TEXTS: CaseNameTexts = {
  untitled: t('webAdmin.kanban.untitled', { locale: 'cs' }),
  untitledHint: (id: string) => t('webAdmin.kanban.untitledHint', { locale: 'cs', values: { id } }),
};

export function caseDisplayName(
  item: { readonly id: string; readonly title: string; readonly propertyId: string | null },
  addressByProperty: ReadonlyMap<string, string>,
  texts: CaseNameTexts,
): CaseDisplayName {
  if (!isInternalIdTitle(item.title)) return { text: item.title.trim(), hint: undefined };
  const address = item.propertyId === null ? undefined : addressByProperty.get(item.propertyId);
  if (address !== undefined && address.trim() !== '') return { text: address.trim(), hint: undefined };
  return { text: texts.untitled, hint: texts.untitledHint(item.id) };
}
